(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define(['underscore', 'torso/modules/NestedModel', 'services/messageExtService'], factory);
  } else if (typeof exports === 'object') {
    // CommonJS-style exports
    var messageExtService = require('./../urls').messageExtService;
    module.exports = factory(require('underscore'), require('backbone-torso/modules/NestedModel'), messageExtService);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function(_, TorsoNestedModel, messageExtService) {
  'use strict';

  var MESSAGE_STATUSES = {
    NEW: 'NEW',
    UNREAD: 'UNREAD',
    READ: 'READ'
  };
  var MESSAGE_TYPES = {
    APPOINTMENT: 'APPOINTMENT',
    BILL: 'BILL',
    RECEIPT: 'RECEIPT',
    PHARMACY: 'PHARMACY',
    SURVEY: 'SURVEY',
    LINK: 'LINK'
  };
  var BILL_STATUSES = {
    PAID: 'PAID',
    UNPAID: 'UNPAID',
    DISPUTED: 'DISPUTED',
    PAYMENT_PENDING: 'PAYMENT_PENDING'
  };

  /**
   * Model representing a secure message.
   *
   * @class SecureMessageModel
   * @constructor
   * @extends Torso.NestedModel
   */
  var SecureMessageModel = TorsoNestedModel.extend({
    idAttribute: 'servicesId',

    /**
     * @property MESSAGE_STATUSES {Object} hash from named secure message status to value of the status in a secure message.
     *   @property MESSAGE_STATUSES.NEW {String} a message that is new and not read.
     *   @property MESSAGE_STATUSES.UNREAD {String} a message that is not read.
     *   @property MESSAGE_STATUSES.READ {String} a message that is read.
     */
    MESSAGE_STATUSES: MESSAGE_STATUSES,

    /**
     * @property MESSAGE_TYPES {Object} hash from named secure message type to value of the type in a secure message.
     *   @property MESSAGE_STATUSES.NEW {String} a message that is new and not read.
     *   @property MESSAGE_STATUSES.UNREAD {String} a message that is not read.
     *   @property MESSAGE_STATUSES.READ {String} a message that is read.
     *   @property MESSAGE_TYPES.APPOINTMENT {String} APPOINTMENT
     *   @property MESSAGE_TYPES.BILL {String} BILL
     *   @property MESSAGE_TYPES.RECEIPT {String} RECEIPT
     *   @property MESSAGE_TYPES.PHARMACY {String} PHARMACY
     *   @property MESSAGE_TYPES.SURVEY {String} SURVEY
     *   @property MESSAGE_TYPES.LINK {String} LINK
     */
    MESSAGE_TYPES: MESSAGE_TYPES,

    /**
     * @property BILL_STATUSES {Object} hash from named secure message status to value of the status in a secure message.
     *   @property BILL_STATUSES.PAID {String} The bill is considered paid in the hospital's system.
     *   @property BILL_STATUSES.UNPAID {String} The bill is not fully paid in the hospital's system.
     *   @property BILL_STATUSES.DISPUTED {String} The bill is disputed.
     *   @property BILL_STATUSES.PAYMENT_PENDING {String} A payment has been made on the bill in our system and is awaiting
     *                                                    confirmation that it has been paid in the hospital's system.
     */
    BILL_STATUSES: BILL_STATUSES,

    /**
     * @method _isMessageUnread
     * @param currentUsername the username of the user to check if the message is read for.
     * @param accessibleUsernames a collection of usernames that the viewing user has access to.
     * @returns {Boolean} true if unread, false otherwise.
     * @private
     */
    _isMessageUnread: function (currentUsername, accessibleUsernames) {
      var i, username,
          usernameStatus = this.get('usernameStatus');

      if (currentUsername && usernameStatus[currentUsername]
        && (usernameStatus[currentUsername] === MESSAGE_STATUSES.NEW || usernameStatus[currentUsername] === MESSAGE_STATUSES.UNREAD)) {
        return true;
      }
      if (accessibleUsernames) {
        for (i = 0; i < accessibleUsernames.length; i++) {
          username = accessibleUsernames[i];
          if (usernameStatus[username] && (usernameStatus[username] === MESSAGE_STATUSES.NEW || usernameStatus[username] === MESSAGE_STATUSES.UNREAD)) {
            return true;
          }
        }
      }
      return false;
    },

    /**
     * If this is a billMessage, returns whether this bill is past due.
     * If this is not a billMessage, or the bill has no dueDate, returns false.
     * @method isPastDue
     * @return {Boolean} whether the bill is pastDue
     */
    isPastDue: function () {
      var billDTO = this.get('billDTO');
      if (billDTO && billDTO.externalBill.dueDate && billDTO.externalBill.status !== BILL_STATUSES.PAID) {
        return new Date(billDTO.externalBill.dueDate) < new Date();
      }
      return false;
    },

    /**
     * If this is a billMessage, returns an object with the bill startDate and endDate
     * If the start date and end date are on the same day, the output object will have only a startDate
     * If this is not a billMessage or the bill has no serviceDate, returns an empty object.
     * @method getServiceDates
     * @return {Object} that contains the startDate and endDate of the bills services
     */
    getServiceDates: function() {
      var serviceDates, serviceStartDate, serviceEndDate,
          output = {},
          billDTO = this.get('billDTO');
      if (billDTO && billDTO.externalBill.serviceDate) {
        serviceDates = billDTO.externalBill.serviceDate.split('-');
        output.startDate = serviceDates[0];
        serviceStartDate = new Date(parseInt(serviceDates[0]));
        serviceEndDate = new Date(parseInt(serviceDates[1]));
        //Only set an end date if it is on a different day than the start date
        if (serviceStartDate.toDateString() !== serviceEndDate.toDateString()) {
          output.endDate = serviceDates[1];
        }
      }
      return output;
    },

    /**
     * Mark the secure message as read
     * @method markAsRead
     * @return {Deferred} Promise from ajax request
     */
    markAsRead: function() {
      var model = this;
      var data = {
        id: this.id,
        type: this.get('type')
      };
      $.ajax({
        method: 'PUT',
        url: messageExtService + '/messageRead',
        data: JSON.stringify(data),
        dataType: 'json',
        contentType: 'application/json'
      }).then(function() {
        model.trigger('usernameStatusUpdated');
      });
    },

    /**
     * Returns the identifier for this secure message model in the format of id-{vecnaId}.
     * @method getIdentifier
     * @returns {string}
     */
    getIdentifier: function() {
      return 'id-' + this.get('vecnaId');
    }
  });

  SecureMessageModel.MESSAGE_STATUSES = MESSAGE_STATUSES;
  SecureMessageModel.MESSAGE_TYPES = MESSAGE_TYPES;
  SecureMessageModel.BILL_STATUSES = BILL_STATUSES;

  return SecureMessageModel;
}));
